import React, { useEffect, useRef, useState } from 'react';
import {
    createBrowserRouter,
    Navigate,
    Outlet,
    RouterProvider,
    useLocation
} from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { handleZoomCallback } from './components/ZoomAuth';
import { handleGoogleCallback } from './components/GoogleAuth';
import { handleMicrosoftCallback } from './components/MicrosoftAuth';

// Revised Components
import PasscodeAuthRevised from './components/PasscodeAuth-revised';
import RoleSelectionRevised from './components/RoleSelection-revised';
import TranslationHostRevised from './components/TranslationHost-revised';
import TranslationReceiverRevised from './components/TranslationReceiver-revised';
import GlossaryManagement from './components/GlossaryManagement';
import AdminSettings from './components/AdminSettings';
import FindUserByHandle from './components/FindUserByHandle';
import PromptSettings from './components/PromptSettings';
import ApiTestHarness from './components/ApiTestHarness';
import ScenarioTesting from './components/ScenarioTesting';
import QaSessionLab from './components/QaSessionLab';
import ManualQaAdminWorkspace from './components/ManualQaAdminWorkspace';
import ManualQaWorkspace from './components/ManualQaWorkspace';
import GlobalLogout from './components/GlobalLogout';
import { canAccessManualQaAdmin, getManualQaRoutePolicy, isManualQaTesterOnly } from './manualQaRoutePolicy';

// Legacy Components (kept for guides)
import HostGuide from './components/HostGuide';
import ReceiverGuide from './components/ReceiverGuide';
import HostControl from './components/HostControl';

import { canReadPlatformAdmin } from './utils/platformAccess';

// Styles
import './index-revised.css';

function ProtectedRoute({ children, requiredRole, authorizeUser }) {
    const { authenticated, loading, user } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'var(--bg-app)',
                color: 'var(--text-secondary)'
            }}>
                Loading...
            </div>
        );
    }

    if (!authenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Default-deny for Manual QA tester-only identities: a provisioned tester (C2HL Member,
    // no admin access) may reach ONLY the /manual-qa runner. Any other internal route sends
    // them there, so the AdminPortal never exposes its admin surface to an external tester.
    if (isManualQaTesterOnly(user) && location.pathname !== '/manual-qa') {
        return <Navigate to="/manual-qa" replace />;
    }

    if (authorizeUser && !authorizeUser(user)) {
        return <Navigate to="/" replace />;
    }

    // Platform admins (SuperAdmin, IsPlatformAdmin) and Admin role have access to everything
    const hasPlatformAccess = user?.IsPlatformAdmin || user?.Role === 'SuperAdmin' || user?.Role === 'Admin';

    if (requiredRole && user?.Role !== requiredRole && !hasPlatformAccess) {
        // If user doesn't have required role (and is not a platform admin), redirect to home
        return <Navigate to="/" replace />;
    }

    return children;
}

function ManualQaRoute() {
    const policy = getManualQaRoutePolicy('runner');

    if (policy.bypassAuth) {
        return <ManualQaWorkspace />;
    }

    return (
        <ProtectedRoute requiredRole={policy.requiredRole}>
            <ManualQaWorkspace />
        </ProtectedRoute>
    );
}

function ManualQaAdminRoute() {
    const policy = getManualQaRoutePolicy('admin');

    if (policy.bypassAuth) {
        return <ManualQaAdminWorkspace />;
    }

    return (
        <ProtectedRoute authorizeUser={canAccessManualQaAdmin}>
            <ManualQaAdminWorkspace />
        </ProtectedRoute>
    );
}

export function LoginRoute() {
    const { authenticated, login } = useAuth();
    const location = useLocation();
    const [callbackError, setCallbackError] = useState('');
    const [isHandlingCallback, setIsHandlingCallback] = useState(false);
    const [callbackProvider, setCallbackProvider] = useState('');
    // A `{provider}_state` is a one-time bearer credential: once redeemed or expired it never
    // becomes valid again, so it gets exactly one exchange attempt no matter how often these
    // effects re-run. The guard has to be the state value, not a boolean — a boolean that the
    // failure path resets is itself a re-arm, which is how one dead Google state was redeemed
    // 30 times over 68 seconds in production on 2026-08-29. `window.history.replaceState` in
    // the catch does not update the react-router `location` these effects depend on, so the
    // param is still there on every re-run.
    const attemptedStateRef = useRef(null);

    // Handle Zoom OAuth callback: backend redirects back to /login?zoom_state={state}
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const zoomState = searchParams.get('zoom_state');
        if (!zoomState || authenticated || attemptedStateRef.current === zoomState) {
            return;
        }
        attemptedStateRef.current = zoomState;

        setIsHandlingCallback(true);
        setCallbackProvider('Zoom');
        setCallbackError('');

        handleZoomCallback(zoomState, (jwtToken, user) => {
            console.log('[App] Zoom OAuth callback success');
            // Clean the ?zoom_state= param from the URL so a refresh or back-navigation
            // does not trigger a second (failed) attempt against an already-consumed state.
            window.history.replaceState({}, '', window.location.pathname);
            login(jwtToken, user, null);
        }).catch((err) => {
            console.error('[App] Zoom OAuth callback failed:', err);
            // Clean the URL even on failure so retry does not re-use the consumed state.
            window.history.replaceState({}, '', window.location.pathname);
            setCallbackError('Zoom sign-in failed. Please try again.');
        }).finally(() => {
            setIsHandlingCallback(false);
        });
    }, [location.search, authenticated, login]);

    // Handle Google OAuth callback: backend redirects back to /login?google_state={state}
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const googleState = searchParams.get('google_state');
        if (!googleState || authenticated || attemptedStateRef.current === googleState) {
            return;
        }
        attemptedStateRef.current = googleState;

        setIsHandlingCallback(true);
        setCallbackProvider('Google');
        setCallbackError('');

        handleGoogleCallback(googleState, (jwtToken, user) => {
            console.log('[App] Google OAuth callback success');
            window.history.replaceState({}, '', window.location.pathname);
            login(jwtToken, user, null);
        }).catch((err) => {
            console.error('[App] Google OAuth callback failed:', err);
            window.history.replaceState({}, '', window.location.pathname);
            setCallbackError('Google sign-in failed. Please try again.');
        }).finally(() => {
            setIsHandlingCallback(false);
        });
    }, [location.search, authenticated, login]);

    // Handle Microsoft OAuth callback: backend redirects back to /login?microsoft_state={state}
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const microsoftState = searchParams.get('microsoft_state');
        if (!microsoftState || authenticated || attemptedStateRef.current === microsoftState) {
            return;
        }
        attemptedStateRef.current = microsoftState;

        setIsHandlingCallback(true);
        setCallbackProvider('Microsoft');
        setCallbackError('');

        handleMicrosoftCallback(microsoftState, (jwtToken, user) => {
            console.log('[App] Microsoft OAuth callback success');
            window.history.replaceState({}, '', window.location.pathname);
            login(jwtToken, user, null);
        }).catch((err) => {
            console.error('[App] Microsoft OAuth callback failed:', err);
            window.history.replaceState({}, '', window.location.pathname);
            setCallbackError('Microsoft sign-in failed. Please try again.');
        }).finally(() => {
            setIsHandlingCallback(false);
        });
    }, [location.search, authenticated, login]);

    if (authenticated) {
        const from = location.state?.from;
        const redirectTarget = from && from.pathname !== '/login'
            ? `${from.pathname || '/'}${from.search || ''}${from.hash || ''}`
            : '/';
        return <Navigate to={redirectTarget} replace />;
    }

    if (isHandlingCallback) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'var(--bg-app)',
                color: 'var(--text-secondary)'
            }}>
                {/* TODO: extract to i18n when AdminPortal adds i18n support */}
                Signing you in with {callbackProvider}&hellip;
            </div>
        );
    }

    return <PasscodeAuthRevised initialError={callbackError} />;
}

function RootLayout() {
    return (
        <>
            <GlobalLogout />
            <Outlet />
        </>
    );
}

const router = createBrowserRouter(
    [
        {
            element: <RootLayout />,
            children: [
                { path: '/login', element: <LoginRoute /> },
                {
                    path: '/',
                    element: (
                        <ProtectedRoute>
                            <RoleSelectionRevised />
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/host',
                    element: (
                        <ProtectedRoute>
                            <TranslationHostRevised />
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/glossary',
                    element: (
                        <ProtectedRoute requiredRole="Host">
                            <GlossaryManagement />
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/admin-settings',
                    // Platform readers and up (level 3), the same rule the page applies. The
                    // generic requiredRole="Admin" let a tenant Admin in and kept PlatformReadOnly
                    // out, so the read-only modes built for that role never ran.
                    element: (
                        <ProtectedRoute authorizeUser={canReadPlatformAdmin}>
                            <AdminSettings />
                        </ProtectedRoute>
                    )
                },
                {
                    // Sprint S7 — admin-only support tool: look up a user by
                    // the permanent QolSync handle they quote from iOS / web
                    // Settings. PlatformReadOnly is sufficient (read-only
                    // operation; matches backend AdminUserLookupFunctions
                    // MinimumAdminRoleLevel = 3).
                    path: '/find-user',
                    element: (
                        <ProtectedRoute authorizeUser={canReadPlatformAdmin}>
                            <div style={{ padding: '2rem', background: 'var(--bg-app)', minHeight: '100vh' }}>
                                <FindUserByHandle />
                            </div>
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/prompt-settings',
                    element: (
                        <ProtectedRoute requiredRole="Admin">
                            <PromptSettings />
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/api-harness',
                    element: (
                        <ProtectedRoute requiredRole="SuperAdmin">
                            <ApiTestHarness />
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/scenario-testing',
                    element: (
                        <ProtectedRoute requiredRole="SuperAdmin">
                            <ScenarioTesting />
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/qa-session-lab',
                    element: (
                        <ProtectedRoute requiredRole="SuperAdmin">
                            <QaSessionLab />
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/manual-qa',
                    element: <ManualQaRoute />
                },
                {
                    path: '/manual-qa/admin',
                    element: <ManualQaAdminRoute />
                },
                { path: '/receiver', element: <TranslationReceiverRevised /> },
                {
                    path: '/host-guide',
                    element: (
                        <ProtectedRoute>
                            <div style={{ padding: '2rem', background: 'var(--bg-app)', minHeight: '100vh' }}>
                                <HostGuide />
                            </div>
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/receiver-guide',
                    element: (
                        <ProtectedRoute>
                            <div style={{ padding: '2rem', background: 'var(--bg-app)', minHeight: '100vh' }}>
                                <ReceiverGuide />
                            </div>
                        </ProtectedRoute>
                    )
                },
                {
                    path: '/host-control',
                    element: (
                        <ProtectedRoute>
                            <HostControl />
                        </ProtectedRoute>
                    )
                },
                { path: '*', element: <Navigate to="/" replace /> }
            ]
        }
    ],
    {
        future: {
            v7_relativeSplatPath: true,
            v7_startTransition: true
        }
    }
);

function AppRevised() {
    return (
        <AuthProvider>
            <RouterProvider router={router} />
        </AuthProvider>
    );
}

export default AppRevised;
